/**
 * 지도앱 딥링크 표준 컨트롤러.
 *
 * 디자이너가 심어둔 컨테이너([data-fw-nav])의 data-* 속성에서 식장 좌표/이름을 읽어
 * Tmap/카카오맵/네이버지도 앱을 길찾기(경로 안내) 상태로 연다. 앱 미설치 시 스토어로 fallback.
 *
 * 동작 방식:
 *   - 안드로이드: intent:// URL 사용. 미설치 시 OS가 자동으로 플레이스토어로 이동
 *     (S.browser_fallback_url). 별도 타이머 불필요.
 *   - iOS: 커스텀 스킴을 직접 호출하고, 1.6초 안에 앱 전환(visibilitychange/
 *     pagehide/blur)이 감지되지 않으면 App Store로 fallback.
 *   - PC: 지도앱이 없으므로 카카오맵/네이버지도는 웹 지도를 새 탭으로 연다.
 *     Tmap은 웹 길찾기 서비스가 없어 "모바일에서 이용" 토스트만 표시.
 *
 *   ⚠️ 옛 버전은 iframe 에 딥링크를 꽂는 방식이었으나, 최신 iOS Safari/Android Chrome
 *   에서 차단되어 항상 스토어로 빠지는 버그가 있었다. 직접 호출 방식으로 교체됨.
 *
 * DOM 규약:
 *   <section data-fw-nav
 *            data-venue-lat="{{ wedding.venue.lat }}"
 *            data-venue-lng="{{ wedding.venue.lng }}"
 *            data-venue-name="{{ wedding.venue.name }}">
 *     <a data-fw-nav-tmap>Tmap</a>
 *     <a data-fw-nav-kakao>카카오맵</a>
 *     <a data-fw-nav-naver>네이버지도</a>
 *   </section>
 *
 * id 폴백: #btnTmap / #btnKakaoNavi(또는 #btnKakaoMap) / #btnNaverMap (기존 디자이너 관습 지원).
 */
export const NAVIGATION_CONTROLLER_SCRIPT = `
<script data-fw-nav-controller>
(function () {
  var container = document.querySelector('[data-fw-nav]');
  var lat = container ? container.getAttribute('data-venue-lat') : null;
  var lng = container ? container.getAttribute('data-venue-lng') : null;
  var name = container ? container.getAttribute('data-venue-name') : '';
  if (!lat || !lng) return;
  var LAT = parseFloat(lat), LNG = parseFloat(lng);
  if (isNaN(LAT) || isNaN(LNG)) return;
  var ENC_NAME = encodeURIComponent(name || '');

  var UA = navigator.userAgent || '';
  var IS_ANDROID = /android/i.test(UA);
  // iPadOS 13+ Safari 는 데스크톱 UA 로 위장 → maxTouchPoints 로 보정
  var IS_IOS = /iphone|ipad|ipod/i.test(UA) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // PC 안내용 토스트 (지도앱 없음)
  var toastTimer = null;
  function showToast(msg) {
    var t = document.getElementById('__fwNavToast');
    if (!t) {
      t = document.createElement('div');
      t.id = '__fwNavToast';
      t.style.cssText = 'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:rgba(0,0,0,0.85);color:#fff;padding:12px 20px;border-radius:8px;font-size:14px;z-index:9999;opacity:0;transition:opacity .2s;pointer-events:none;max-width:80vw;text-align:center;';
      document.body.appendChild(t);
    }
    t.textContent = msg;
    t.style.opacity = '1';
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { t.style.opacity = '0'; }, 2200);
  }

  // 안드로이드: intent:// — 미설치 시 OS 가 browser_fallback_url(플레이스토어)로 자동 이동
  function openAndroid(scheme, path, pkg) {
    var fallback = 'https://play.google.com/store/apps/details?id=' + pkg;
    window.location.href =
      'intent://' + path +
      '#Intent;scheme=' + scheme +
      ';package=' + pkg +
      ';S.browser_fallback_url=' + encodeURIComponent(fallback) +
      ';end';
  }

  // iOS/기타: 딥링크 직접 호출 + 앱 전환 감지. 전환 안 되면 App Store 로 fallback
  function openIOS(deepLink, storeUrl) {
    var switched = false;
    function mark() { switched = true; }
    document.addEventListener('visibilitychange', mark);
    window.addEventListener('pagehide', mark);
    window.addEventListener('blur', mark);
    setTimeout(function () {
      document.removeEventListener('visibilitychange', mark);
      window.removeEventListener('pagehide', mark);
      window.removeEventListener('blur', mark);
      if (!switched && !document.hidden) { window.location.href = storeUrl; }
    }, 1600);
    window.location.href = deepLink;
  }

  // opts: { scheme, path, pkg, iosStore, webUrl? }
  function go(opts) {
    if (IS_ANDROID) {
      openAndroid(opts.scheme, opts.path, opts.pkg);
    } else if (IS_IOS) {
      openIOS(opts.scheme + '://' + opts.path, opts.iosStore);
    } else if (opts.webUrl) {
      window.open(opts.webUrl, '_blank', 'noopener');
    } else {
      showToast('이 지도 길찾기는 모바일 앱에서 이용할 수 있어요.');
    }
  }

  function wire(sel, handler) {
    var els = document.querySelectorAll(sel);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.dataset.fwNavInit) continue;
      el.dataset.fwNavInit = '1';
      el.addEventListener('click', function (e) { e.preventDefault(); handler(); });
    }
  }

  wire('[data-fw-nav-tmap], #btnTmap', function () {
    go({
      scheme: 'tmap',
      path: 'route?goalname=' + ENC_NAME + '&goalx=' + LNG + '&goaly=' + LAT,
      pkg: 'com.skt.tmap.ku',
      iosStore: 'https://apps.apple.com/kr/app/id431589174'
    });
  });
  wire('[data-fw-nav-kakao], #btnKakaoNavi, #btnKakaoMap', function () {
    go({
      scheme: 'kakaomap',
      path: 'route?ep=' + LAT + ',' + LNG + '&by=CAR',
      pkg: 'net.daum.android.map',
      iosStore: 'https://apps.apple.com/kr/app/id304608425',
      // PC: 식장 이름으로 카카오맵 웹 검색
      webUrl: ENC_NAME ? 'https://map.kakao.com/?q=' + ENC_NAME : ''
    });
  });
  wire('[data-fw-nav-naver], #btnNaverMap', function () {
    go({
      scheme: 'nmap',
      path: 'navigation?dlat=' + LAT + '&dlng=' + LNG + '&dname=' + ENC_NAME + '&appname=ai.fiket.wedding',
      pkg: 'com.nhn.android.nmap',
      iosStore: 'https://apps.apple.com/kr/app/id311867728',
      // PC: 식장 이름으로 네이버지도 웹 검색
      webUrl: ENC_NAME ? 'https://map.naver.com/p/search/' + ENC_NAME : ''
    });
  });
})();
</script>
`;
//# sourceMappingURL=navigation.js.map